import { DirectionType, Direction } from '@wui/common-library'
import { Ref, isRef, nextTick, ref } from 'vue'

type Config = {
  event: MouseEvent | TouchEvent,
  container: Ref<HTMLElement | null>,
  popup: Ref<HTMLElement | null>,
  gapX?: number,
  gapY?: number,
  isCentered?: boolean,
}

/**
 * @TODO add support for Vue components for popup (now popup.value.style is undefined)
 *
 * @param config @see Config
 * @returns void
 */
export const usePositionPopUp = async (config: Config) => {
  const { event, container, popup, gapY = 0, gapX = 0, isCentered = false } = config

  await nextTick()
  if (!popup.value || !container.value) {
    return
  }

  const containerRect = container.value.getBoundingClientRect()
  const menuRect = popup.value.getBoundingClientRect()

  let clientY: number
  let clientX: number

  if ('clientY' in event) {
    clientY = event.clientY
    clientX = event.clientX
  } else {
    const touch = event.touches[0]
    clientY = touch?.clientY ?? 0
    clientX = touch?.clientX ?? 0
  }

  const yCoordinate = clientY - containerRect.top
  const xCoordinate = clientX - containerRect.left

  const doesYFit = yCoordinate + menuRect.height < containerRect.height
  const doesXFit = xCoordinate + menuRect.width < containerRect.width

  const top = doesYFit ? yCoordinate - gapY : (yCoordinate - menuRect.height) + gapY
  let left = doesXFit ? xCoordinate - gapX : (xCoordinate - menuRect.width) + gapX
  if (left < 0) {
    left = xCoordinate - gapX
  }

  popup.value.style.top = `${Math.abs(top)}px`
  popup.value.style.left = isCentered ? '50%' : `${Math.abs(left)}px`
  popup.value.style.transform = isCentered ? 'translate(-50%)' : ''
}

type DirectionConfig = {
  activator: Ref<HTMLElement | null>,
  popup: Ref<HTMLElement | null>,
  direction: Ref<DirectionType | undefined> | DirectionType,
  offset?: number,
  clampToViewport?: boolean,
}

const getMenuPosition = (
  directionValue: DirectionType,
  triggerRect: DOMRect,
  menuRect: DOMRect,
  offset: number,
) => {
  const centerX = triggerRect.left + triggerRect.width / 2 - menuRect.width / 2
  const centerY = triggerRect.top + triggerRect.height / 2 - menuRect.height / 2

  const positions: Record<DirectionType, { top: number; left: number }> = {
    [Direction.TOP]: { top: triggerRect.top - menuRect.height - offset, left: centerX },
    [Direction.TOP_LEFT]: { top: triggerRect.top - menuRect.height - offset, left: triggerRect.left },
    [Direction.TOP_RIGHT]: { top: triggerRect.top - menuRect.height - offset, left: triggerRect.right - menuRect.width },
    [Direction.BOTTOM]: { top: triggerRect.bottom + offset, left: centerX },
    [Direction.BOTTOM_LEFT]: { top: triggerRect.bottom + offset, left: triggerRect.left },
    [Direction.BOTTOM_RIGHT]: { top: triggerRect.bottom + offset, left: triggerRect.right - menuRect.width },
    [Direction.LEFT]: { top: centerY, left: triggerRect.left - menuRect.width - offset },
    [Direction.LEFT_TOP]: { top: triggerRect.top, left: triggerRect.left - menuRect.width - offset },
    [Direction.LEFT_BOTTOM]: { top: triggerRect.bottom - menuRect.height, left: triggerRect.left - menuRect.width - offset },
    [Direction.RIGHT]: { top: centerY, left: triggerRect.right + offset },
    [Direction.RIGHT_TOP]: { top: triggerRect.top, left: triggerRect.right + offset },
    [Direction.RIGHT_BOTTOM]: { top: triggerRect.bottom - menuRect.height, left: triggerRect.right + offset },
  }

  return positions[directionValue]
}

export const usePositionPopUpByDirection = (config: DirectionConfig) => {
  const {
    activator,
    popup,
    direction,
    offset = 8,
    clampToViewport = true,
  } = config

  const popupStyles = ref<Record<string, string>>({})

  const updatePosition = async () => {
    await nextTick()
    if (!activator.value || !popup.value) {
      return
    }

    const triggerRect = activator.value.getBoundingClientRect()
    const menuRect = popup.value.getBoundingClientRect()
    const directionValue = isRef(direction) ? (direction.value ?? Direction.TOP_LEFT) : (direction ?? Direction.TOP_LEFT)
    const position = getMenuPosition(directionValue, triggerRect, menuRect, offset)

    let top = position.top
    let left = position.left

    if (clampToViewport) {
      top = Math.min(Math.max(0, top), window.innerHeight - menuRect.height)
      left = Math.min(Math.max(0, left), window.innerWidth - menuRect.width)
    }

    popupStyles.value = {
      top: `${top}px`,
      left: `${left}px`,
    }
  }

  return {
    popupStyles,
    updatePosition,
  }
}
